import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Table,
  Spinner, Input, Label, Container
} from "reactstrap";
import Select from "react-select";
import AccountingReportService from "../../../services/AccountingServices/AccountingReportService";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";

const BalanceSheet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [reportData, setReportData] = useState(null);

  const [filters, setFilters] = useState({
    fiscalYearCode: null,
    asOf: "",
  });

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  const fetchFiscalYears = async () => {
    try {
      const response = await FiscalYearService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setFiscalYears(items.map((fy) => ({ value: fy.code, label: fy.name })));
    } catch (error) {
      console.error("Error fetching fiscal years:", error);
    }
  };

  const handleGenerate = async () => {
    if (!filters.fiscalYearCode) {
      toast.error("Please select a fiscal year");
      return;
    }
    setLoading(true);
    try {
      const params = {
        fiscalYearCode: filters.fiscalYearCode,
        asOf: filters.asOf || undefined,
      };
      const data = await AccountingReportService.getBalanceSheet(params);
      setReportData(data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch balance sheet");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) =>
    (val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: "#dee2e6",
      fontSize: "0.85rem",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    option: (base) => ({ ...base, fontSize: "0.85rem" }),
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Balance Sheet" pageTitle="Accounting" />

        {/* Filters Card */}
        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: 12, overflow: "hidden" }}>
          <CardHeader
            className="border-0 py-3"
            style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="ri-filter-3-line text-primary fs-16"></i>
              <span className="fw-semibold text-dark">Report Filters</span>
            </div>
          </CardHeader>
          <CardBody className="p-3">
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Label className="form-label small fw-semibold text-muted mb-1">Fiscal Year *</Label>
                <Select
                  options={fiscalYears}
                  value={fiscalYears.find((fy) => fy.value === filters.fiscalYearCode)}
                  onChange={(opt) => setFilters((f) => ({ ...f, fiscalYearCode: opt?.value || null }))}
                  placeholder="Select Fiscal Year..."
                  isClearable
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={3}>
                <Label className="form-label small fw-semibold text-muted mb-1">As of Date</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={filters.asOf}
                  onChange={(e) => setFilters((f) => ({ ...f, asOf: e.target.value }))}
                  style={{ borderRadius: 8, height: 38, fontSize: "0.85rem" }}
                />
              </Col>
              <Col md={3}>
                <Button
                  color="primary"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    height: 38, borderRadius: 8, fontWeight: 600,
                    background: "linear-gradient(135deg, #0ab39c 0%, #22d3ee 100%)",
                    border: "none",
                  }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-file-search-line"></i> Generate Report
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {reportData && (
          <>
            {/* Summary Cards */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: 12, background: "rgba(10,179,156,0.06)", borderLeft: "4px solid #0ab39c" }}>
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase">Total Assets</p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#0ab39c" }}>{formatCurrency(reportData.totalAssets)}</h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(10,179,156,0.12)" }}>
                        <i className="ri-bank-line fs-24" style={{ color: "#0ab39c" }}></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: 12, background: "rgba(240,101,72,0.06)", borderLeft: "4px solid #f06548" }}>
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase">Total Liabilities</p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#f06548" }}>{formatCurrency(reportData.totalLiabilities)}</h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(240,101,72,0.12)" }}>
                        <i className="ri-wallet-2-line fs-24" style={{ color: "#f06548" }}></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: 12, background: "rgba(64,81,137,0.06)", borderLeft: "4px solid #405189" }}>
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase">Net Income</p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#405189" }}>{formatCurrency(reportData.netIncome)}</h4>
                      </div>
                      <div className="d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(64,81,137,0.12)" }}>
                        <i className="ri-line-chart-line fs-24" style={{ color: "#405189" }}></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Assets Table */}
            <Row className="g-4">
              <Col lg={6}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: 12, overflow: "hidden" }}>
                  <CardHeader className="bg-light border-0 py-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-arrow-up-circle-line text-success fs-18"></i>
                      <span className="fw-bold text-dark">Assets</span>
                    </div>
                  </CardHeader>
                  <div className="table-responsive">
                    <Table className="table-hover align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                      <thead className="table-light">
                        <tr>
                          <th className="text-muted fw-semibold small text-uppercase">Code</th>
                          <th className="text-muted fw-semibold small text-uppercase">Ledger Name</th>
                          <th className="text-muted fw-semibold small text-uppercase text-end">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.assets?.map((asset) => (
                          <tr key={asset.ledgerId} style={{ cursor: "pointer" }} onClick={() => navigate(`/ledger-statement?ledgerId=${asset.ledgerId}`)}>
                            <td>{asset.ledgerCode}</td>
                            <td className="fw-semibold">{asset.ledgerName}</td>
                            <td className="text-end fw-bold text-success">{formatCurrency(asset.balance)}</td>
                          </tr>
                        ))}
                        {(!reportData.assets || reportData.assets.length === 0) && (
                          <tr><td colSpan={3} className="text-center py-3 text-muted">No assets found</td></tr>
                        )}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan={2} className="text-end fw-bold">Total Assets</td>
                          <td className="text-end fw-bold text-success" style={{ fontSize: "1rem" }}>{formatCurrency(reportData.totalAssets)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </Card>
              </Col>

              {/* Liabilities Table */}
              <Col lg={6}>
                <Card className="shadow-sm border-0 h-100" style={{ borderRadius: 12, overflow: "hidden" }}>
                  <CardHeader className="bg-light border-0 py-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-arrow-down-circle-line text-danger fs-18"></i>
                      <span className="fw-bold text-dark">Liabilities & Equity</span>
                    </div>
                  </CardHeader>
                  <div className="table-responsive">
                    <Table className="table-hover align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                      <thead className="table-light">
                        <tr>
                          <th className="text-muted fw-semibold small text-uppercase">Code</th>
                          <th className="text-muted fw-semibold small text-uppercase">Ledger Name</th>
                          <th className="text-muted fw-semibold small text-uppercase text-end">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.liabilities?.map((liability) => (
                          <tr key={liability.ledgerId} style={{ cursor: "pointer" }} onClick={() => navigate(`/ledger-statement?ledgerId=${liability.ledgerId}`)}>
                            <td>{liability.ledgerCode}</td>
                            <td className="fw-semibold">{liability.ledgerName}</td>
                            <td className="text-end fw-bold text-danger">{formatCurrency(liability.balance)}</td>
                          </tr>
                        ))}
                        <tr className="bg-light">
                          <td colSpan={2} className="fw-semibold">Net Income</td>
                          <td className="text-end fw-bold text-primary">{formatCurrency(reportData.netIncome)}</td>
                        </tr>
                        {(!reportData.liabilities || reportData.liabilities.length === 0) && (
                          <tr><td colSpan={3} className="text-center py-3 text-muted">No liabilities found</td></tr>
                        )}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan={2} className="text-end fw-bold">Total Liabilities & Equity</td>
                          <td className="text-end fw-bold text-danger" style={{ fontSize: "1rem" }}>{formatCurrency(reportData.totalLiabilities + reportData.netIncome)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </Card>
              </Col>
            </Row>

            <div className="mt-4 text-center">
              {Math.abs(reportData.totalAssets - (reportData.totalLiabilities + reportData.netIncome)) < 0.01 ? (
                <Badge color="soft-success" className="px-4 py-2 fs-13 rounded-pill">
                  <i className="ri-checkbox-circle-fill me-1 align-bottom"></i> Balance Sheet is Balanced
                </Badge>
              ) : (
                <Badge color="soft-danger" className="px-4 py-2 fs-13 rounded-pill">
                  <i className="ri-error-warning-fill me-1 align-bottom"></i> Balance Sheet is Unbalanced
                </Badge>
              )}
            </div>
          </>
        )}

        {!reportData && !loading && (
          <Card className="shadow-sm border-0 text-center py-5" style={{ borderRadius: 12 }}>
            <CardBody>
              <div className="mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(10,179,156,0.1)" }}>
                <i className="ri-bank-line fs-36 text-primary"></i>
              </div>
              <h5 className="fw-bold text-dark">Balance Sheet Report</h5>
              <p className="text-muted">Select filters above and click generate to view the report.</p>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default BalanceSheet;
